// Version: 1.3
// v1.3: Recipient box takes its size from index.css (CV-50).
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.1: The Send to address in the Email to client box is editable, filled
//       in from the stored address; edits apply to that email only (app.py
//       v4.06).
// v1.0: One way to generate and send every report type (app.py v4.05).
//       ReportActions puts the same two buttons on each report section:
//       Generate (opens Reports and Assessments with that type and client
//       chosen) and Email to client (opens SendReportDialog). The dialog shows
//       which report goes to which stored address before anything is sent.
//       Used by ClientManagement.js, ClientDetail.js and NewAssessment.js.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from './auth';

export const REPORT_KINDS = {
  it:         { title: 'IT and Cyber Security Report',       newType: 'it_report',  generate: 'Generate',
                send: (slug, info, filename, to) => ({ url: `/api/clients/${slug}/email-report`, body: { to } }) },
  analytics:  { title: 'Website Analytics Report',           newType: 'analytics',  generate: 'Generate',
                send: (slug, info, filename, to) => ({ url: `/api/clients/${slug}/analytics/report/send`, body: { frequency: info?.frequency || 'monthly', recipients: to } }) },
  assessment: { title: 'External Cyber Security Assessment', newType: 'assessment', generate: 'Generate',
                send: (slug, info, filename, to) => ({ url: `/api/clients/${slug}/assessment-reports/email`, body: { filename: filename || info?.latest?.filename || '', to } }) },
};

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, width: '100%',
              maxWidth: 460, padding: '20px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', color: 'var(--text)' };
const label = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 };

export function SendReportDialog({ slug, kind, filename, onClose, onSent }) {
  const k = REPORT_KINDS[kind];
  const [info, setInfo]   = useState(null);
  const [err, setErr]     = useState('');
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState('');
  const [to, setTo]       = useState('');   // v1.1: editable, prefilled with the stored address(es)

  useEffect(() => {
    authFetch(`/api/clients/${slug}/report-recipients?kind=${kind}`)
      .then(r => r.json().then(d => (r.ok ? d : Promise.reject(new Error(d.error || 'Could not load the recipients')))))
      .then(d => { setInfo(d); setTo((d.recipients || []).join(', ')); })
      .catch(e => setErr(e.message));
  }, [slug, kind]);

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const list = to.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
      const { url, body } = k.send(slug, info, filename, list);
      const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || d.message || 'The email could not be sent');
      setDone(d.message || 'Sent.');
      if (onSent) onSent(d.message);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const reportName = filename || info?.latest?.filename;
  const hasReport  = kind === 'analytics' || !!reportName;
  const toCount    = to.split(/[,;\s]+/).filter(Boolean).length;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Email the {k.title}</div>
        {!info && !err && <div style={{ padding: 12 }}><span className="spinner" /></div>}
        {info && !done && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={label}>Client</div>
              <div style={{ fontSize: 13 }}>{info.client_name}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={label}>Report</div>
              {kind === 'analytics'
                ? <div style={{ fontSize: 13 }}>{info.frequency === 'weekly' ? 'Last 7 days' : 'Last 30 days'}, built when you send it</div>
                : reportName
                  ? <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{reportName}{!filename && info.latest?.date ? <span style={{ color: 'var(--text-muted)' }}> · {fmtWhen(info.latest.date)}</span> : null}</div>
                  : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>None yet</div>}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={label}>Send to</div>
              <input type="text" value={to} onChange={e => setTo(e.target.value)} placeholder="name@company.com"
                style={{ width: '100%' }} autoFocus />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {info.recipients.length
                  ? <>Filled in from the {info.source.toLowerCase()}. Separate several addresses with commas. Changes here apply to this email only; to change the stored address use the client's <Link to={info.settings_link} onClick={onClose} style={{ color: 'var(--accent)' }}>Settings</Link>{kind === 'analytics' && <> or <Link to="/schedules" onClick={onClose} style={{ color: 'var(--accent)' }}>Schedules</Link></>}.</>
                  : <>No address is stored for this client, so type one. To save one for next time, use the client's <Link to={info.settings_link} onClick={onClose} style={{ color: 'var(--accent)' }}>Settings</Link>.</>}
              </div>
            </div>
            {info.note && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>{info.note}</div>}
            {!hasReport && <div className="error-msg" style={{ marginBottom: 12 }}>There's no report to send yet. Generate one first.</div>}
          </>
        )}
        {done && <div className="success-msg" style={{ marginBottom: 12 }}>{done}</div>}
        {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
          <button className="btn btn-secondary" onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
          {!done && <button className="btn btn-primary" onClick={send} disabled={busy || !info || !hasReport || !toCount}>
            {busy ? <span className="spinner" /> : (toCount > 1 ? `Send to ${toCount} addresses` : 'Send')}
          </button>}
        </div>
      </div>
    </div>
  );
}

// The same two actions on every report section
export function ReportActions({ slug, kind, onSent, style }) {
  const [open, setOpen] = useState(false);
  const k = REPORT_KINDS[kind];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', ...style }}>
      <Link className="btn btn-secondary btn-sm" to={`/new?type=${k.newType}&for=${encodeURIComponent(slug)}`}
        title={`Open Reports and Assessments with the ${k.title} and this client chosen`}>
        <i className="ti ti-file-plus" /> {k.generate}
      </Link>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)} title={`Email the ${k.title} to the client`}>
        <i className="ti ti-mail" /> Email to client
      </button>
      {open && <SendReportDialog slug={slug} kind={kind} onClose={() => setOpen(false)} onSent={onSent} />}
    </div>
  );
}
